import { useState, useEffect } from 'react'
import { Navigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Mail, ArrowRight } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/constants/routes'
import { authService } from '@/services/authService'
import { toast } from 'sonner'

export default function ForgotPasswordPage() {
  const { t } = useTranslation()
  const { isAuth } = useAuth()

  const [email, setEmail]       = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError]       = useState(null)
  const [success, setSuccess]   = useState(false)
  const [mounted, setMounted]   = useState(false)

  useEffect(() => { setMounted(true) }, [])

  if (isAuth) return <Navigate to={ROUTES.DASHBOARD} replace />

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!email) return
    setIsLoading(true)
    setError(null)
    try {
      await authService.forgotPassword(email)
      setSuccess(true)
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #000666 0%, #1A237E 50%, #000444 100%)' }}
    >
      {/* Background decorative blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,109,0,0.4) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-60 -left-20 w-[600px] h-[600px] rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(27,109,36,0.5) 0%, transparent 70%)' }}
        />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Card */}
      <div
        className={`relative z-10 w-full max-w-md mx-4 transition-all duration-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        {/* Logo section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <span
              className="text-4xl font-black text-white"
              style={{ fontFamily: 'Cairo, sans-serif', letterSpacing: '-0.02em' }}
            >
              Hagtna
            </span>
            <span
              className="w-2 h-2 rounded-full bg-orange-DEFAULT inline-block"
              style={{ background: '#FF6D00' }}
            />
            <span
              className="text-4xl font-black text-white"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              حاجاتنا
            </span>
          </div>
          <div
            className="inline-block px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
            style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.8)',
              fontFamily: 'Tajawal, sans-serif',
            }}
          >
            {t('app.admin')}
          </div>
        </div>

        {/* Card */}
        <div
          className="rounded-[24px] p-8"
          style={{
            background: 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(32px)',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 32px 64px rgba(0,0,0,0.3)',
          }}
        >
          <h2
            className="text-2xl font-black text-white mb-1"
            style={{ fontFamily: 'Cairo, sans-serif' }}
          >
            {t('auth.forgotPassword', 'Forgot Password')}
          </h2>
          <p className="text-white/60 text-sm mb-8" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            {t('auth.forgotPasswordSubtitle', 'Enter your email to receive a reset link')}
          </p>

          {/* Error alert */}
          {error && (
            <div
              className="flex items-center gap-3 p-3 rounded-xl mb-6 animate-in"
              style={{ background: 'rgba(186,26,26,0.15)', border: '1px solid rgba(186,26,26,0.3)' }}
            >
              <AlertCircle size={18} style={{ color: '#FFDAD6', flexShrink: 0 }} />
              <span className="text-sm" style={{ color: '#FFDAD6', fontFamily: 'Tajawal, sans-serif' }}>
                {error}
              </span>
            </div>
          )}
          
          {success ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 text-green-400 mb-4">
                <Mail size={32} />
              </div>
              <p className="text-white mb-6" style={{ fontFamily: 'Tajawal, sans-serif' }}>
                {t('auth.resetLinkSent', 'If that email is registered, a reset link has been sent.')}
              </p>
              <Link
                to={ROUTES.LOGIN}
                className="inline-flex items-center justify-center gap-2 text-sm font-bold text-orange-DEFAULT hover:text-orange-400 transition-colors"
                style={{ fontFamily: 'Tajawal, sans-serif', color: '#FF6D00' }}
              >
                {t('auth.backToLogin', 'Back to login')} <ArrowRight size={16} className="rtl:rotate-180" />
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'rgba(255,255,255,0.8)', fontFamily: 'Cairo, sans-serif' }}
                >
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@hagatna.com"
                  required
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                    fontFamily: 'Tajawal, sans-serif',
                    textAlign: 'right',
                  }}
                  onFocus={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.15)'
                    e.target.style.border = '1px solid rgba(255,109,0,0.6)'
                  }}
                  onBlur={(e) => {
                    e.target.style.background = 'rgba(255,255,255,0.1)'
                    e.target.style.border = '1px solid rgba(255,255,255,0.2)'
                  }}
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading || !email}
                className="w-full py-3.5 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all mt-2"
                style={{
                  background: isLoading ? 'rgba(255,109,0,0.6)' : '#FF6D00',
                  fontFamily: 'Cairo, sans-serif',
                  fontSize: '16px',
                  boxShadow: '0 8px 24px rgba(255,109,0,0.35)',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transform: isLoading ? 'none' : undefined,
                }}
              >
                {isLoading ? (
                  <>
                    <span
                      className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                      style={{ animation: 'spin 0.8s linear infinite' }}
                    />
                    {t('auth.sending', 'Sending...')}
                  </>
                ) : (
                  t('auth.sendResetLink', 'Send Reset Link')
                )}
              </button>
              
              <div className="text-center mt-4">
                <Link
                  to={ROUTES.LOGIN}
                  className="text-sm font-bold text-white/60 hover:text-white transition-colors"
                  style={{ fontFamily: 'Tajawal, sans-serif' }}
                >
                  {t('auth.backToLogin', 'Back to login')}
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p
          className="text-center mt-6 text-white/30 text-xs"
          style={{ fontFamily: 'Tajawal, sans-serif' }}
        >
          Hagatna Multi-Vendor Platform — Admin Panel v1.0
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </div>
  )
}
